import { useState, useCallback } from 'react'
import {
  Bold,
  Italic,
  Underline,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Link2,
  Unlink,
  Table,
  Image,
  Youtube,
  Undo,
  Redo,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { ToolbarButton } from './ToolbarButton'
import { ToolbarDivider } from './ToolbarDivider'
import { LinkDialog } from './LinkDialog'
import { TableDialog } from './TableDialog'

export function Toolbar({ editor, className }) {
  const [linkDialogOpen, setLinkDialogOpen] = useState(false)
  const [tableDialogOpen, setTableDialogOpen] = useState(false)

  const handleSetLink = useCallback((url) => {
    if (url) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
    }
    setLinkDialogOpen(false)
  }, [editor])

  const handleRemoveLink = useCallback(() => {
    editor.chain().focus().unsetLink().run()
  }, [editor])

  const handleAddImage = useCallback(() => {
    const url = window.prompt('Enter image URL:')
    if (url) {
      editor.chain().focus().setImage({ src: url }).run()
    }
  }, [editor])

  const handleAddYoutube = useCallback(() => {
    const url = window.prompt('Enter YouTube URL:')
    if (url) {
      editor.chain().focus().setYoutubeVideo({ src: url }).run()
    }
  }, [editor])

  const handleInsertTable = useCallback(({ rows, cols }) => {
    editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run()
    setTableDialogOpen(false)
  }, [editor])

  if (!editor) return null

  return (
    <>
      <div
        className={cn(
          'flex flex-wrap items-center gap-1 border-b bg-muted/30 p-2',
          className
        )}
      >
        {/* Undo/Redo */}
        <ToolbarButton
          icon={Undo}
          label="Undo"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
        />
        <ToolbarButton
          icon={Redo}
          label="Redo"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
        />

        <ToolbarDivider />

        {/* Text Formatting */}
        <ToolbarButton
          icon={Bold}
          label="Bold (Cmd+B)"
          isActive={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        />
        <ToolbarButton
          icon={Italic}
          label="Italic (Cmd+I)"
          isActive={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        />
        <ToolbarButton
          icon={Underline}
          label="Underline (Cmd+U)"
          isActive={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        />

        <ToolbarDivider />

        {/* Headings */}
        <ToolbarButton
          icon={Heading1}
          label="Heading 1"
          isActive={editor.isActive('heading', { level: 1 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        />
        <ToolbarButton
          icon={Heading2}
          label="Heading 2"
          isActive={editor.isActive('heading', { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        />
        <ToolbarButton
          icon={Heading3}
          label="Heading 3"
          isActive={editor.isActive('heading', { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        />

        <ToolbarDivider />

        {/* Lists */}
        <ToolbarButton
          icon={List}
          label="Bullet List"
          isActive={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        />
        <ToolbarButton
          icon={ListOrdered}
          label="Numbered List"
          isActive={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        />

        <ToolbarDivider />

        {/* Block Elements */}
        <ToolbarButton
          icon={Quote}
          label="Blockquote"
          isActive={editor.isActive('blockquote')}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        />
        <ToolbarButton
          icon={Link2}
          label="Add Link"
          isActive={editor.isActive('link')}
          onClick={() => setLinkDialogOpen(true)}
        />
        {editor.isActive('link') && (
          <ToolbarButton
            icon={Unlink}
            label="Remove Link"
            onClick={handleRemoveLink}
          />
        )}

        <ToolbarDivider />

        {/* Table */}
        <ToolbarButton
          icon={Table}
          label="Insert Table"
          onClick={() => setTableDialogOpen(true)}
        />

        <ToolbarDivider />

        {/* Media */}
        <ToolbarButton
          icon={Image}
          label="Insert Image"
          onClick={handleAddImage}
        />
        <ToolbarButton
          icon={Youtube}
          label="Insert YouTube Video"
          onClick={handleAddYoutube}
        />
      </div>

      <LinkDialog
        open={linkDialogOpen}
        onOpenChange={setLinkDialogOpen}
        onSubmit={handleSetLink}
        initialUrl={editor.getAttributes('link').href || ''}
      />

      <TableDialog
        open={tableDialogOpen}
        onOpenChange={setTableDialogOpen}
        onSubmit={handleInsertTable}
      />
    </>
  )
}
